<script lang="ts" setup>
import { refDebounced } from "@vueuse/core";
import { ref } from "vue";

import ProgressLoader from "#components/layout/ProgressLoader.vue";
import ErrorAlert from "#components/typography/ErrorAlert.vue";
import { appIcons } from "#composables/use-icons";
import { useSeasonsQuery } from "#modules/season/season.queries";
import type { Season } from "#modules/season/season.types";
import { createGetElId } from "#utilities/element";
import { getError } from "#utilities/error";

export interface SeasonSearchDialogProps {
  /** Current season ID (for display) */
  seasonId: string | undefined;
}

const props = defineProps<SeasonSearchDialogProps>();

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/prefer-function-type
  (e: "select", season: Season): void;
}>();

/** Get HTML element ID (with shared component prefix) */
const getElId = createGetElId("season-search-dialog");

const openModel = defineModel<boolean>();

const seasonSearchRaw = ref("");
const seasonSearch = refDebounced(seasonSearchRaw, 300, { maxWait: 750 });

const {
  data: seasonsData,
  error: seasonsError,
  isLoading: seasonsLoading,
} = useSeasonsQuery({
  perPage: 5,
  search: seasonSearch,
});

const getIsSeasonCurrent = (season: Season) => season.id === props.seasonId;

const handleSelection = (season: Season) => {
  emit("select", season);
  openModel.value = false;
};
</script>

<template>
  <VDialog v-model="openModel" max-width="450" v-bind="$attrs">
    <VCard>
      <VCardTitle>Change Season</VCardTitle>
      <VCardText class="py-0 pb-1 px-4">
        <ProgressLoader v-if="seasonsLoading" />
        <ErrorAlert v-else-if="seasonsError" :text="getError(seasonsError)" />
        <template v-else-if="seasonsData">
          <VTextField
            :id="getElId('search-txt')"
            v-model="seasonSearchRaw"
            density="comfortable"
            hide-details
            label="Search"
            :prepend-inner-icon="appIcons.mdiSearch"
          />
          <VList v-if="seasonsData.items.length" class="mt-2 py-0" density="comfortable">
            <VListItem
              v-for="season in seasonsData.items"
              :id="getElId('season-list-item', season.id)"
              :key="season.id"
              :active="getIsSeasonCurrent(season)"
              :append-icon="getIsSeasonCurrent(season) ? appIcons.mdiCheckCircle : undefined"
              color="primary"
              rounded
              @click="handleSelection(season)"
            >
              <VListItemTitle
                class="d-flex align-center ga-1"
                :class="{ 'font-weight-bold': getIsSeasonCurrent(season) }"
              >
                <span :class="{ 'opacity-80': !season.current }">
                  {{ season.name }}
                </span>
                <VBadge
                  v-if="!season.current"
                  v-tooltip="'Non-active'"
                  class="d-flex"
                  color="warning"
                  dot
                  inline
                />
              </VListItemTitle>
            </VListItem>
          </VList>
          <div v-else class="mt-4 text-disabled text-center">No seasons found</div>
        </template>
        <slot name="footer" v-bind="{ error: seasonsError, loading: seasonsLoading }" />
      </VCardText>
      <VCardActions>
        <ActionBar right>
          <VBtn :id="getElId('actions-close-btn')" @click="openModel = false">Close</VBtn>
        </ActionBar>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
